import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, Heart, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const StarRow = ({ value, size = 'w-4 h-4' }) => {
  const rounded = Math.round(value || 0);
  return (
    <span className="inline-flex items-center" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= rounded ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </span>
  );
};

const Reviews = () => {
  const [gbp, setGbp] = useState(null);
  const [gbpError, setGbpError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/reviews/google`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => setGbp(data))
      .catch(() => setGbpError(true));
  }, []);

  // Review platforms with direct links
  const reviewPlatforms = [
    {
      name: 'Google',
      icon: '🔍',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      description: 'Most important for local search visibility',
      reviewUrl: 'https://search.google.com/local/writereview?placeid=ChIJKYgN8OemTIYRwHJpZ3g_Paw',
      profileUrl: 'https://www.google.com/maps/place/103+NE+1st+Ave,+Mineral+Wells,+TX+76067',
      priority: 'high',
      instructions: 'Click the button below, sign in with your Google account, and leave your review!'
    },
    {
      name: 'Facebook',
      icon: '📘',
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      description: 'Share your experience with friends',
      reviewUrl: 'https://www.facebook.com/profile.php?id=61559570946841&sk=reviews',
      profileUrl: 'https://www.facebook.com/profile.php?id=61559570946841',
      priority: 'high',
      instructions: 'Visit our Facebook page and click "Reviews" to share your experience!'
    },
    {
      name: 'Yelp',
      icon: '⭐',
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      description: 'Help foodies discover us',
      reviewUrl: 'https://www.yelp.com/writeareview/biz/1st-ave-icp-mineral-wells',
      profileUrl: 'https://www.yelp.com/biz/1st-ave-icp-mineral-wells',
      priority: 'high',
      instructions: 'Create a free Yelp account (if needed) and share your honest review!'
    },
    {
      name: 'TripAdvisor',
      icon: '🦉',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: 'Great for visitors & tourists',
      reviewUrl: 'https://www.tripadvisor.com/UserReviewEdit-g56481-d26834751-1st_Ave_ICP-Mineral_Wells_Texas.html',
      profileUrl: 'https://www.tripadvisor.com/Restaurant_Review-g56481-d26834751-Reviews-1st_Ave_ICP-Mineral_Wells_Texas.html',
      priority: 'medium',
      instructions: 'Sign in to TripAdvisor and rate your experience!'
    },
    {
      name: 'Instagram',
      icon: '📷',
      color: 'from-pink-500 to-purple-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      description: 'Tag us in your photos!',
      reviewUrl: 'https://www.instagram.com/1staveicp/',
      profileUrl: 'https://www.instagram.com/1staveicp/',
      priority: 'medium',
      instructions: 'Post a photo, tag @1staveicp, and use #1stAveICP!'
    }
  ];

  // Sample review prompts to help customers
  const reviewPrompts = [
    "What was your favorite ice cream flavor?",
    "How was the atmosphere and service?",
    "Would you recommend us to friends?",
    "What made your visit special?",
    "Did you try our famous Stackers or Nachos?"
  ];

  return (
    <>
      <Helmet>
        <title>Leave a Review | 1st Ave ICP - Mineral Wells, TX</title>
        <meta name="description" content="Love 1st Ave ICP? Leave us a review on Google, Facebook, Yelp, or TripAdvisor! Your feedback helps us serve you better and helps others discover us." />
        <link rel="canonical" href="https://1staveicp.com/reviews" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4"
        style={{
          background: 'linear-gradient(135deg, #E91E8C 0%, #C71585 50%, #9B1B6E 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-4">💝</div>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            We'd Love Your Feedback!
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Had a great experience? Your review helps us grow and helps others discover the best ice cream in Mineral Wells!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section 
        className="py-12 md:py-16 px-4"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-5xl mx-auto">
          
          {/* Live Google Reviews */}
          {gbp && gbp.configured && (gbp.reviews?.length > 0 || gbp.rating) && (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-10" data-testid="google-reviews-section">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <img
                    src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
                    alt="Google"
                    className="h-6"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-3xl font-bold text-gray-900" data-testid="google-rating-value">
                        {gbp.rating ? gbp.rating.toFixed(1) : '—'}
                      </span>
                      <StarRow value={gbp.rating} size="w-5 h-5" />
                    </div>
                    <p className="text-sm text-gray-500">
                      Based on {gbp.total_ratings ?? 0} Google reviews
                    </p>
                  </div>
                </div>
                {gbp.google_maps_url && (
                  <a
                    href={gbp.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid="view-on-google-link"
                  >
                    View on Google <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {gbp.reviews.map((rev, idx) => (
                  <a
                    key={idx}
                    href={rev.google_maps_url || gbp.google_maps_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-gray-50 rounded-xl p-5 hover:shadow-md transition-all"
                    data-testid={`google-review-card-${idx}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {rev.author_photo ? (
                        <img
                          src={rev.author_photo}
                          alt={rev.author_name}
                          className="w-10 h-10 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-pink-200 flex items-center justify-center font-bold text-pink-700">
                          {(rev.author_name || 'G')[0]}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{rev.author_name}</p>
                        <div className="flex items-center gap-2">
                          <StarRow value={rev.rating} />
                          <span className="text-xs text-gray-400">{rev.relative_time}</span>
                        </div>
                      </div>
                    </div>
                    {rev.text && (
                      <p className="text-sm text-gray-600 line-clamp-5">{rev.text}</p>
                    )}
                  </a>
                ))}
              </div>
              <p className="text-center text-xs text-gray-400 mt-5">
                Reviews are ordered by Google by relevance and powered by Google.
              </p>
            </div>
          )}

          {/* Why Reviews Matter */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-10 text-center">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Why Your Review Matters
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Supports Local Business</h3>
                <p className="text-sm text-gray-600">Your reviews help a family-owned business thrive in Mineral Wells</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Helps Others Discover Us</h3>
                <p className="text-sm text-gray-600">New customers find us through your honest recommendations</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Improves Our Service</h3>
                <p className="text-sm text-gray-600">Your feedback helps us serve you even better</p>
              </div>
            </div>
          </div>

          {/* Review Platforms */}
          <div className="mb-10">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              📝 Leave a Review
            </h2>
            
            {/* High Priority Platforms */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {reviewPlatforms.filter(p => p.priority === 'high').map((platform) => (
                <div 
                  key={platform.name}
                  className={`${platform.bgColor} border-2 ${platform.borderColor} rounded-2xl p-6 hover:shadow-lg transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-4xl">{platform.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{platform.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{platform.description}</p>
                      <p className="text-xs text-gray-500 mb-4">{platform.instructions}</p>
                      <a
                        href={platform.reviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${platform.color} text-white font-bold rounded-full hover:opacity-90 transition-opacity`}
                      >
                        <Star className="w-4 h-4" />
                        Leave Review
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Medium Priority Platforms */}
            <div className="grid md:grid-cols-2 gap-4">
              {reviewPlatforms.filter(p => p.priority === 'medium').map((platform) => (
                <div 
                  key={platform.name}
                  className={`${platform.bgColor} border ${platform.borderColor} rounded-xl p-4 hover:shadow-md transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{platform.icon}</span>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{platform.name}</h3>
                      <p className="text-xs text-gray-600">{platform.description}</p>
                    </div>
                    <a
                      href={platform.reviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1 px-4 py-2 bg-gradient-to-r ${platform.color} text-white text-sm font-bold rounded-full hover:opacity-90 transition-opacity`}
                    >
                      Review
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Review Writing Tips */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-10">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              💡 What to Include in Your Review
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {reviewPrompts.map((prompt, index) => (
                <div 
                  key={index}
                  className="bg-pink-50 rounded-xl p-4 text-center"
                >
                  <p className="text-gray-700 font-medium">{prompt}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-500 text-sm mt-6">
              Be honest and specific - your genuine experience helps others make decisions!
            </p>
          </div>

          {/* Thank You & Social */}
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-2xl p-8 text-center text-white">
            <h2 
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              Thank You! 🍦
            </h2>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              We appreciate you taking the time to share your experience. Every review helps our small business grow!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/menu"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-pink-600 font-bold rounded-full hover:bg-pink-50 transition-colors"
              >
                View Our Menu
              </Link>
              <Link
                to="/specials"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-pink-700 text-white font-bold rounded-full hover:bg-pink-800 transition-colors"
              >
                See Current Specials
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Reviews;
